"use client";

import {useState, useRef, useEffect} from "react";
import Link from "next/link";
import {useRouter} from "next/navigation";
import {Bell} from "lucide-react";
import api from "@/lib/api";

interface Notification {
  id: string;
  type: string;
  title: string;
  body?: string;
  link?: string;
  is_read: boolean;
  created_at: string;
}

interface NotificationBellProps {
  user?: {id: string};
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function NotificationBell(_props?: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const response = await api.get("/notifications/unread-count");
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      // Silently fail - don't break the UI
      console.error("Failed to fetch unread count:", err);
    }
  };

  // Fetch recent notifications when dropdown opens
  const fetchNotifications = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await api.get("/notifications?limit=10");
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch unread count on mount and periodically
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.is_read) {
      try {
        await api.post("/notifications/mark-read", {ids: [notification.id]});
        setNotifications(
          notifications.map((n) =>
            n.id === notification.id ? {...n, is_read: true} : n
          )
        );
        setUnreadCount(Math.max(0, unreadCount - 1));
      } catch (err) {
        console.error("Failed to mark notification as read:", err);
      }
    }

    // Navigate to link if available
    if (notification.link) {
      setIsOpen(false);
      router.push(notification.link);
    } else {
      // Default to notifications page
      setIsOpen(false);
      router.push("/dashboard/notifications");
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-10 h-10 rounded-full border transition-colors hover:opacity-90"
        style={{
          borderColor: "rgba(212, 175, 55, 0.3)",
          backgroundColor: "rgba(212, 175, 55, 0.1)",
        }}
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" style={{color: "#D4AF37"}} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold rounded-full"
            style={{
              backgroundColor: "#dc2626",
              color: "white",
            }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50 max-h-[500px] flex flex-col"
          style={{borderColor: "rgba(10, 55, 64, 0.1)"}}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="px-4 py-3 border-b flex items-center justify-between"
            style={{borderColor: "rgba(10, 55, 64, 0.1)"}}
          >
            <h3 className="font-semibold" style={{color: "var(--pnn-green)"}}>
              Notifications
            </h3>
            {notifications.length > 0 && (
              <Link
                href="/dashboard/notifications"
                onClick={() => setIsOpen(false)}
                className="text-sm"
                style={{color: "var(--pnn-gold)"}}
              >
                View all
              </Link>
            )}
          </div>

          {/* Notifications list */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div
                className="px-4 py-8 text-center text-sm"
                style={{color: "var(--pnn-text-muted)"}}
              >
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div
                className="px-4 py-8 text-center text-sm"
                style={{color: "var(--pnn-text-muted)"}}
              >
                No notifications
              </div>
            ) : (
              <div className="py-1">
                {notifications.map((notification) => (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full text-left px-4 py-3 border-b transition-colors ${
                      !notification.is_read ? "bg-blue-50" : "hover:bg-gray-50"
                    }`}
                    style={{borderColor: "rgba(10, 55, 64, 0.05)"}}
                  >
                    <div className="flex items-start gap-3">
                      {!notification.is_read && (
                        <div
                          className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                          style={{backgroundColor: "var(--pnn-gold)"}}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-medium ${
                            !notification.is_read ? "font-semibold" : ""
                          }`}
                          style={{color: "var(--pnn-text-dark)"}}
                        >
                          {notification.title}
                        </p>
                        {notification.body && (
                          <p
                            className="text-xs mt-1 line-clamp-2"
                            style={{color: "var(--pnn-text-muted)"}}
                          >
                            {notification.body}
                          </p>
                        )}
                        <p
                          className="text-xs mt-1"
                          style={{color: "var(--pnn-text-muted)"}}
                        >
                          {formatTimeAgo(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div
              className="px-4 py-2 border-t"
              style={{borderColor: "rgba(10, 55, 64, 0.1)"}}
            >
              <Link
                href="/dashboard/notifications"
                onClick={() => setIsOpen(false)}
                className="block text-center text-sm py-2 rounded transition-colors hover:bg-gray-50"
                style={{color: "var(--pnn-gold)"}}
              >
                View all notifications
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
